import { AxiosRequestConfig } from 'axios';
import { useCallback, useMemo, useState } from 'react';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { ProjectId, TaskId } from 'api/models';
import { Project } from 'api/project';
import { StatusGroup, Task, TaskAPI, TaskUpdatePhasePayload } from 'api/tasks';
import { toastService } from 'components/molecules/toast';
import {
  converToEntityId,
  PhaseEntityType,
} from 'components/organisms/projects/project-plan/phase-entity';
import { RootState } from 'data';
import { Actor, ActorName } from 'data/core';
import { saveTaskDetailSaga } from 'data/tasks/actions';
import { ApiErrorData } from 'queries/error';
import { logger } from 'services';
import { FieldsStore } from 'stores/fields';
import { queueMacrotask } from 'utils/macrotask';
import { negativeUid } from 'utils/uid';
import { safelyParseJSON } from 'utils/window-utils';
import {
  getProjectDependencyMapKey,
  getProjectDetailQueryKey,
  getProjectTasksQueryKey,
  ProjectTasksQueryKey,
} from './common';
import { tenMinutes } from './constants';

type Tasks = Task[];

export const useProjectTasksQuery = (
  projectId: ProjectId,
  options: UseQueryOptions<Tasks, unknown, Tasks, ProjectTasksQueryKey> = {},
  config?: AxiosRequestConfig,
): UseQueryResult<Tasks> => {
  return useQuery(
    getProjectTasksQueryKey(projectId),
    () => TaskAPI.getTaskList(projectId[1], config),
    {
      staleTime: tenMinutes,
      cacheTime: 0,
      enabled: !!projectId[1] && projectId[1] > 0,
      ...options,
    },
  );
};

type TaskDetailQueryKey = [string, number, number];
export const getTaskDetailQueryKey = ([
  ,
  projectId,
  taskId,
]: TaskId): TaskDetailQueryKey => ['task-detail', projectId, taskId];

export const useUpdatePhaseTaskMutation = (
  taskId: number,
): UseMutationResult<Task, unknown, TaskUpdatePhasePayload> => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation<Task, unknown, TaskUpdatePhasePayload>(
    (payload) => TaskAPI.updatePhase(taskId, payload),
    {
      onSuccess: async (task) => {
        const {
          project: { projectId },
        } = task;

        dispatch(saveTaskDetailSaga(task));
        await queryClient.setQueryData(
          getTaskDetailQueryKey(TaskId(projectId, taskId)),
          task,
        );

        await queryClient.refetchQueries(
          getProjectTasksQueryKey(ProjectId(projectId)),
        );
        await queryClient.invalidateQueries(
          getProjectDetailQueryKey(ProjectId(projectId)),
        );

        await queryClient.invalidateQueries(
          getProjectDependencyMapKey(projectId),
        );
      },
    },
  );
};

export const useTaskDetailQuery = (
  taskId: TaskId,
  queryOptions: UseQueryOptions<
    Task,
    ApiErrorData,
    Task,
    TaskDetailQueryKey
  > = {},
): UseQueryResult<Task, ApiErrorData> => {
  return useQuery(
    getTaskDetailQueryKey(taskId),
    () => TaskAPI.getTask(taskId[1], taskId[2]),
    {
      retry: 2,
      ...queryOptions,
    },
  );
};

export const useCreateTaskOptimisticMutation = (
  projectId: ProjectId,
  phaseId?: number,
) => {
  const queryClient = useQueryClient();
  const fieldsStore = useSelector(({ fields }: RootState) => fields);
  const taskStatusField = useMemo(
    () => FieldsStore.getTaskStatusField(fieldsStore),
    [fieldsStore],
  );

  const [tasksInCreation, setTasksInCreation] = useState<Tasks>([]);

  const createTask = (payload) => {
    const tempTaskId = negativeUid();

    setTasksInCreation((prev) => [
      ...prev,
      {
        ...payload,
        taskId: tempTaskId,
        fields: [
          {
            ...taskStatusField[1],
            fieldValue: StatusGroup.Todo,
          },
        ],
        project: {
          projectId: projectId[1],
        },
      } as Task,
    ]);

    queueMacrotask(() => {
      TaskAPI.createTask(projectId[1], payload)
        .then((newTask) => {
          const previousProjectData = queryClient.getQueryData<Project>(
            getProjectDetailQueryKey(projectId),
          );
          const previousProjectTasksData = queryClient.getQueryData<Tasks>(
            getProjectTasksQueryKey(projectId),
          );

          // optimistic update for tasks
          const optimisticTasks = previousProjectTasksData
            ? [...previousProjectTasksData, newTask]
            : [newTask];

          // optimistic update task order for project phase
          const optimisticProjectData = {
            ...previousProjectData!,
            projectPhases: previousProjectData!.projectPhases.map((phase) => {
              if (phase.projectPhaseId === phaseId) {
                return {
                  ...phase,
                  aggregateProgressValues: {
                    ...phase.aggregateProgressValues,
                    taskCount:
                      (phase?.aggregateProgressValues?.taskCount ?? 0) + 1,
                  },
                  totalTaskCount: (phase.totalTaskCount ?? 0) + 1,
                  entityOrder: phase.entityOrder
                    ? [
                        ...phase.entityOrder,
                        converToEntityId(newTask.taskId, PhaseEntityType.Task),
                      ]
                    : [converToEntityId(newTask.taskId, PhaseEntityType.Task)],
                };
              }

              return phase;
            }),
          };

          queryClient.setQueryData<Project>(
            getProjectDetailQueryKey(projectId),
            optimisticProjectData,
          );

          queryClient.setQueryData<Tasks>(
            getProjectTasksQueryKey(projectId),
            optimisticTasks as Tasks,
          );
        })
        .catch((e) => {
          logger.error('Error creating task', { error: e });
          toastService({
            title: 'Failed to create task',
            message: '',
          }).error();
        })
        .finally(() => {
          setTasksInCreation((prev) =>
            prev.filter((t) => t.taskId !== tempTaskId),
          );
        });
    });
  };

  return { tasksInCreation, createTask };
};

type TaskUpdate = [number];
export const useTaskUpdate = ([projectId]: TaskUpdate) => {
  const queryClient = useQueryClient();

  const onFetchTaskSuccess = useCallback(
    (newTask: Task | undefined) => {
      queryClient.setQueryData(
        getProjectTasksQueryKey(ProjectId(projectId)),
        (prevTasks: Task[] | undefined) =>
          newTask
            ? prevTasks?.map((prevTask: Task) => {
                return prevTask.taskId === newTask.taskId ? newTask : prevTask;
              }) ?? []
            : prevTasks ?? [],
      );
    },
    [projectId, queryClient],
  );

  return { onFetchTaskSuccess };
};

export const useTaskPaneUpdate = (taskId: TaskId) => {
  const [, projectId, taskIdFromParam] = taskId;
  const queryClient = useQueryClient();

  const skipRefetch = window.location.pathname.includes(
    `/projects/${projectId}/plan`,
  );

  const taskDetailQueryKey = useMemo(() => {
    return getTaskDetailQueryKey(['task_id', projectId, taskIdFromParam]);
  }, [projectId, taskIdFromParam]);

  const onTaskUpdateSuccess = useCallback(
    async (newTask: Task) => {
      queryClient.setQueryData(taskDetailQueryKey, newTask);
    },
    [taskDetailQueryKey, queryClient],
  );

  const handleTaskSocketUpdate = useCallback(
    async (socketData) => {
      const {
        data: { taskId: taskIdReceivedFromSocket },
        actor,
      } = socketData;
      const { actorName, actorId } = safelyParseJSON(actor) ?? ({} as Actor);

      if (
        (actorName !== ActorName.TaskDrawer ||
          actorId !== taskIdReceivedFromSocket) &&
        taskIdReceivedFromSocket === taskIdFromParam
      ) {
        queryClient.refetchQueries(taskDetailQueryKey);
      }
    },
    [taskDetailQueryKey, taskIdFromParam, queryClient],
  );

  return { handleTaskSocketUpdate, onTaskUpdateSuccess, skipRefetch };
};
